"use client";

import { Pause } from "lucide-react";
import { playToggleA11y } from "@/lib/a11y/play-toggle";

interface TPlayProps {
  onClick?: () => void;
  playing: boolean;
  size?: number;
  /** Subject of the control (normally the track title) for the stable name. */
  title?: string;
}

export function TPlay({ onClick, playing, size = 80, title }: TPlayProps) {
  return (
    <button type="button"
      onClick={onClick}
      // Playback state is exposed via aria-pressed with a stable name (TES-1581).
      {...playToggleA11y({ playing, title })}
      className="motion-safe:active:scale-[0.97] tlsr-focus"
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        // ONE PlayButton across the system (this matched the row control in
        // waveform-band.tsx): at rest a 1.5px ink RING on the panel ground with
        // the filled triangle as the single filled glyph; playing a filled
        // accent circle with brand-fg pause bars. It used to be a solid ink
        // disc at rest, which read as a second filled primary beside the accent
        // Download pill. The raised shadow is gone too: shadows belong to the
        // four floating surfaces (sheet, modal, mini player, consent), never to
        // a control sitting on a page panel.
        background: playing ? "var(--tls-brand)" : "transparent",
        color: playing ? "var(--tls-brand-fg)" : "var(--tls-fg-strong)",
        border: playing
          ? "1.5px solid transparent"
          : "1.5px solid var(--tls-fg-strong)",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "scale var(--tls-duration-fast) var(--tls-ease)",
        position: "relative",
      }}
    >
      {playing ? (
        <Pause style={{ width: size * 0.36, height: size * 0.36 }} />
      ) : (
        // Tesla-style T-shaped play (vertical stroke + crossbar + triangle)
        <svg width={size * 0.4} height={size * 0.4} viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5l12 7-12 7V5z" />
        </svg>
      )}
    </button>
  );
}
